import { useSelector } from "react-redux"
import { useEffect, useState } from "react"
import { useMutation } from "@tanstack/react-query"

import { API_OPERATIONS_URL } from "../constants"
import {
  IOperationItem,
  IPageResponse,
  IErrorResponse,
} from "../interface"
import { RootState } from "../../store/store"
import { checkIsFailure } from "../utils/utils"
import { axios } from "../axios"

export type IGroupedOperationsList = {
  [key: string]: IOperationItem[]
}

async function fetchOperationsList(
  token: string | null,
  limit: number | undefined,
  offset: number | undefined,
) {
  const response = await axios(
    `${API_OPERATIONS_URL}?limit=${limit}&offset=${offset}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  )

  if (checkIsFailure(response.status)) {
    throw new Error("Ошибка при получении списка операций")
  }

  return response.data
}

const groupOperationList = (
  response: IPageResponse<IOperationItem> | undefined,
  prevData: IGroupedOperationsList[] | undefined,
) => {
  const data: IGroupedOperationsList[] = prevData || []
  response?.items.forEach((operation) => {
    const operationDate = new Date(operation.updatedOn)
    const date = operationDate.setHours(0, 0, 0, 0).toString()
    if (data.find((dayData) => Object.keys(dayData)[0] === date)) {
      data.find((dayData) => Object.keys(dayData)[0] === date)![date].push(operation)
    } else {
      data.push({
        [date]: [operation],
      })
    }
  })

  return data
}

export const useGetOperationsHistory = () => {
  const token = useSelector((state: RootState) => state.auth.token)
  const [currentOffset, setCurrentOffset] = useState(0)
  const [groupedOperationsList, setOperationList] = useState<IGroupedOperationsList[]>([])
  const [rest, setRest] = useState<number>(0)

  const { mutate: updateData, isPending } = useMutation<
    IPageResponse<IOperationItem>,
    IErrorResponse,
    number
  >({ mutationFn: (offset) => fetchOperationsList(token, 100, offset) })

  useEffect(() => {
    updateData(0, {
      onSuccess: (data) => {
        const grouped = groupOperationList(data, groupedOperationsList)
        setRest(data.total - 100)
        setOperationList([...grouped])
      },
    })
  }, [])

  const handleUpdate = () => {
    setCurrentOffset(currentOffset + 100)
    updateData(currentOffset + 100, {
      onSuccess: (data) => {
        if (rest) {
          const grouped = groupOperationList(data, groupedOperationsList)
          setOperationList([...grouped])
        }
        setRest(rest - 100)
      },
    })
  }

  return {
    groupedOperations: groupedOperationsList.sort(
      (a, b) => Number(Object.keys(b)[0]) - Number(Object.keys(a)[0]),
    ),
    handleUpdate,
    isLoading: isPending,
    restOperations: rest,
  }
}
